import { Button, DatePicker, DayOfWeek, ITextField, Label, SearchBox, Stack, TextField } from '@fluentui/react';
import * as React from 'react';
import AutocompleteSearchBox, { ISuggestionItem } from '../../../../Components/AutocompleteSearchBox';
import { IUserListItem } from '../../../../Models/Admin/IUserListItem';
import { IInvoice } from '../../../../Models/Invoice/IInvoice';
import { StornoStateEnum } from '../../../../Models/Invoice/StornoStateEnum';
import { CzDatePickerStrings, parseDateFromString } from '../../../../Tools/DateTime';
import { InvoiceSumaryView } from '../../../Invoices/MyInvoices/InvoiceSumaryView';

interface IInvoiceFormProps {
    invoice?: IInvoice;
    isLoading: boolean;
    isReadOnly: boolean;
    propertyChanged: (key: string, value: any) => void;
    setCustomer: (customer: IUserListItem) => void;
    findCustomer: (query: string) => Promise<IUserListItem[] | undefined>
    createStorno: (invoiceId: number) => void;
    cancelStorno: (invoiceId: number) => void;
    
}

class CustomerSuggestionItem implements ISuggestionItem {

    name: string;
    data: IUserListItem;
    constructor(_name: string, _data: IUserListItem) {
        this.name = _name;
        this.data = _data;
    }

    getSuggestionItem(query?: string | undefined): JSX.Element
    {
        return <>{this.name}</>
    }
    getSearchText() {
        return this.name
    }
}

export const InvoiceForm: React.FunctionComponent<IInvoiceFormProps> = (props) => {

    const [textInput, setTextInput] = React.useState<ITextField | null>(null);
    const [disabled, setDisabled] = React.useState(true);
    const [customerSuggestionsProgress, setSustomerSuggestionsProgress] = React.useState(false);

    React.useEffect(() => {
        if (props.invoice !== undefined) {
            setDisabled(props.invoice?.exported === true || props.invoice?.storno !== StornoStateEnum.None)
        }
        else {
            setDisabled(true)
        }
    }, [props.invoice])    



    const [suggestions, setSuggestions] = React.useState<ISuggestionItem[]>();
    const onChange = async (newText?: string) => {
        if (!newText || newText.trim().length < 3) {
            setSuggestions(undefined);
        } else {
            setSustomerSuggestionsProgress(true);

            const customers = await props.findCustomer(newText);
            if (customers) {
                var suggestion = customers.map(item => {
                    return new CustomerSuggestionItem(item.company !== undefined && item.company?.length > 0 ? item.company: item.firstName + " " + item.lastName,item);
                });
                setSuggestions(suggestion);
            }
           
            setSustomerSuggestionsProgress(false);
        }
    };

    const onSuggestionClicked = (suggestion: string | ISuggestionItem) => {
        if (suggestion instanceof CustomerSuggestionItem) {
            props.setCustomer(suggestion.data);
        }
        textInput?.focus();
    };

    const onFormatDate = (date?: Date): string => {
        return !date ? '' : date.getDate() + '.' + (date.getMonth() + 1) + '.' + (date.getFullYear());
    };

    const textChanged = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        if (event?.target) {
            var id = event.currentTarget.id;
            props.propertyChanged(id, newValue);
        }
    }

    const tokens = { childrenGap: 5 };
    return (<Stack tokens={tokens} styles={{ root: { paddingTop: 20 } }}>
        <Stack horizontal tokens={tokens} verticalAlign="end">
            <TextField readOnly key="invoiceNo" id="invoiceNo" label="Doklad" value={props.invoice?.id !== 0 ? props.invoice?.invoiceNo : "Nová faktura"} disabled={props.isLoading} />
            <TextField key="variabil" id="variabil" label="Variabil" value={props.invoice?.variabil} disabled={props.isLoading} readOnly={props.isReadOnly || disabled} onChange={textChanged} />
            <Stack grow horizontalAlign="end" horizontal>
                {props.invoice?.storno !== StornoStateEnum.Storno ?
                    <Button text={props.invoice?.storno === StornoStateEnum.None ? "Vytvořit storno" : "Zrušit storno"}
                        onClick={() => {
                            if (props.invoice?.storno === StornoStateEnum.None) {
                                props.createStorno(props.invoice.id);
                            }
                            if (props.invoice?.storno === StornoStateEnum.Waiting) {
                                props.cancelStorno(props.invoice.id);
                            }
                        }}
                    />
                    : null}
             </Stack>
            
        </Stack>
        <Stack horizontal tokens={tokens}>
            <Stack.Item>
                <AutocompleteSearchBox
                    inProgress={customerSuggestionsProgress}
                    readOnly={props.isReadOnly || disabled} 
                    disabled={props.isLoading}
                    debounceTime={250}
                    className="search-box"    
                    key="ic" id="id" label="IČ" value={props.invoice?.ic}
                    onSuggestionClicked={onSuggestionClicked}
                    onChange={(_, newValue) => {
                        onChange(newValue);
                    }}
                    suggestions={suggestions}/>
            </Stack.Item>
            <Stack.Item grow>
                <TextField componentRef={(input) => setTextInput(input)} key="name" id="name" label="Název" value={props.invoice?.name} disabled={props.isLoading} readOnly={props.isReadOnly || disabled} onChange={textChanged} />
            </Stack.Item>
        </Stack>

        {/*<TextField key="dic" id="dic" label="DIČ" value={props.invoice?.dic} />*/}
        <Stack tokens={tokens} >
            <TextField key="street" id="street" label="Ulice" value={props.invoice?.street} disabled={props.isLoading} readOnly={props.isReadOnly || disabled} onChange={textChanged} />
        <Stack horizontal tokens={tokens}>
            <Stack.Item>
                    <TextField key="postalCode" id="postalCode" label="PSČ" value={props.invoice?.postalCode} disabled={props.isLoading} readOnly={props.isReadOnly || disabled} onChange={textChanged} />
            </Stack.Item>
            <Stack.Item grow>
                    <TextField key="city" id="city" label="Obec" value={props.invoice?.city} disabled={props.isLoading} readOnly={props.isReadOnly || disabled} onChange={textChanged} />
            </Stack.Item>
            </Stack>
        </Stack>

        <Stack tokens={tokens} >
            <Stack horizontal tokens={tokens}>
                <Stack.Item grow={50} styles={{ root: { maxWidth:'50%' } }}>
                    <DatePicker styles={{ root: { width: '100%' } }}
                        label="Vystavena"
                        disabled={true}
                        value={props.invoice?.created}
                        allowTextInput
                        firstDayOfWeek={DayOfWeek.Monday}
                        firstWeekOfYear={1}
                        placeholder="Vyberte datum narození"
                        ariaLabel="Vyberte datum narození"
                        strings={CzDatePickerStrings}
                        formatDate={onFormatDate}
                        parseDateFromString={(dateStr: string) => parseDateFromString(dateStr, props.invoice?.created ? props.invoice?.created : new Date(Date.now()))}
                    />

                </Stack.Item>
                <Stack.Item grow={50} styles={{ root: { maxWidth: '50%' } }}>
                    <DatePicker styles={{ root: { width: '100%' } }}
                        label="Datum uskut."
                        disabled={props.isLoading || props.isReadOnly || disabled}
                        value={props.invoice?.uskut as Date}
                        onSelectDate={(date: Date | null | undefined) => { props.propertyChanged("uskut", date); }}
                        allowTextInput
                        firstDayOfWeek={DayOfWeek.Monday}
                        firstWeekOfYear={1}
                        placeholder="Vyberte datum uskut. plnění"
                        ariaLabel="Vyberte datum uskut. plnění"
                        strings={CzDatePickerStrings}
                        formatDate={onFormatDate}
                        parseDateFromString={(dateStr: string) => parseDateFromString(dateStr, props.invoice?.uskut ? props.invoice?.uskut as Date: new Date(Date.now()))}
                    />
                </Stack.Item>
            </Stack>
            <Stack horizontal tokens={tokens}>
                <Stack.Item grow styles={{ root: { maxWidth: '50%' } }}>
                    <DatePicker styles={{ root: { width: '100%' } }}
                        label="Splatnost"
                        disabled={props.isLoading || props.isReadOnly || disabled}
                        value={props.invoice?.due as Date}
                        onSelectDate={(date: Date | null | undefined) => { props.propertyChanged("due", date); }}
                        allowTextInput
                        firstDayOfWeek={DayOfWeek.Monday}
                        firstWeekOfYear={1}
                        placeholder="Vyberte datum splatnosti"
                        ariaLabel="Vyberte datum splatnost"
                        strings={CzDatePickerStrings}
                        formatDate={onFormatDate}
                        parseDateFromString={(dateStr: string) => parseDateFromString(dateStr, props.invoice?.due ? props.invoice?.due as Date : new Date(Date.now()))}
                    />
                </Stack.Item>
                <Stack.Item grow styles={{ root: { maxWidth: '50%' } }}>                    
                    <DatePicker styles={{ root: { width: '100%' } }}
                        
                            label="Zaplaceno dne"
                            disabled={true}
                            value={props.invoice?.maxPaidDate}
                            allowTextInput
                            firstDayOfWeek={DayOfWeek.Monday}
                            firstWeekOfYear={1}
                            placeholder="Vyberte datum zaplacení"
                            ariaLabel="Vyberte datum zaplacení"
                            strings={CzDatePickerStrings}
                            formatDate={onFormatDate}
                            parseDateFromString={(dateStr: string) => parseDateFromString(dateStr, props.invoice?.maxPaidDate ? props.invoice?.maxPaidDate : new Date(Date.now()))}
                        />
                </Stack.Item>
            </Stack>
        </Stack>

        <Label>Rekapitulace faktury</Label>
        <InvoiceSumaryView invoice={props.invoice }/>

    </Stack>)
}
 